import { useEffect, useState, useContext, useMemo } from 'react'

import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime'

import { useBucket } from '@mc/experiments-react'
import { useStaticData } from '@mc/static-data-react'
import { GrowthCategory, GrowthContent, SingleSessionResult } from '@mc/api'
import {
  getPageSource,
  inferPageSource,
  PageSource,
  PrePaywallPageSource,
  trackExperimentViewed,
} from '@mc/track-event'
import { paths } from '@mc/paths'
import { useCookieRedirectHref } from '@mc/growth'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { usePromotion } from '@mc/promo-utils'

import { PageDataContext } from 'pages/categories/CategoryPage/components/PageDataProvider/PageDataProvider'

import { QUIZ_PRIMER_FLAG } from './constants'
import { type QuizPrimerType } from './types'

export const usePen5557Exp = () => {
  const isPen5557Variant = useBucket(QUIZ_PRIMER_FLAG)?.includes('variant')
  const validCriteriaForHref = useValidCriteriaForButtonHref()

  useEffect(() => {
    if (validCriteriaForHref) {
      trackExperimentViewed(QUIZ_PRIMER_FLAG)
    }
  }, [validCriteriaForHref])

  return {
    isPen5557Variant,
    shouldOverrideCtaHref: validCriteriaForHref && isPen5557Variant,
  }
}

export const useTrackPen5557Experiment = () => {
  useEffect(() => {
    trackExperimentViewed(QUIZ_PRIMER_FLAG)
  }, [])
}

const useValidCriteriaForButtonHref = () => {
  const routerContext = useContext(RouterContext)
  const [isValid, setIsValid] = useState(false)

  useEffect(() => {
    if (!routerContext) {
      return
    }

    const { pathname } = window.location
    const source = getPageSource()

    if (
      (
        [
          PageSource.SUBCATEGORY,
          PageSource.CATEGORY,
          PageSource.CLASS_PAGE,
          PageSource.SERIES_PAGE,
          PageSource.SESSIONS_CLASS,
        ] as PrePaywallPageSource[]
      ).includes(source) &&
      // This experiment would not work in the all categories page, so we exclude it
      pathname !== paths.categories
    ) {
      setIsValid(true)
    }
  }, [routerContext])

  return isValid
}

export const useQuizPrimerCta = () => {
  const [ctaHref, setCtaHref] = useState<string | null>(null)
  const { shouldOverrideCtaHref } = usePen5557Exp()
  const { item, itemType, category } = useQuizPrimerItem()
  const href = useSpringboardTileHref(
    item?.uuid,
    itemType as QuizPrimerType,
    category?.id,
  )

  useEffect(() => {
    if (shouldOverrideCtaHref) {
      setCtaHref(href)
    }
  }, [href, shouldOverrideCtaHref])

  return {
    ctaHref,
  }
}

export const useSpringboardTileHref = (
  itemUuid?: string,
  itemType?: QuizPrimerType,
  categoryId?: number,
) => {
  const { isPen5557Variant } = usePen5557Exp()
  const { isPromotionOn } = usePromotion()
  const cookieRedirectHref = useCookieRedirectHref()
  const addItemHref = isPen5557Variant && itemUuid && itemType

  return useMemo(() => {
    if (cookieRedirectHref) {
      return cookieRedirectHref
    }

    if (addItemHref) {
      const params = new URLSearchParams()

      params.set(QUIZ_QUERY_PARAMS.SPRINGBOARD_ITEM_ID, itemUuid)
      params.set(QUIZ_QUERY_PARAMS.SPRINGBOARD_ITEM_TYPE, itemType)

      // Add primary category ID to preselect the corresponding motivation
      if (categoryId) {
        params.set(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES, categoryId.toString())
      }

      const classRecommendationsUrl = `${paths.classRecommendations}?${params.toString()}`

      return isPromotionOn
        ? paths.promotionRedirect(classRecommendationsUrl)
        : classRecommendationsUrl
    }

    return isPromotionOn
      ? paths.promotionRedirect(paths.classRecommendations)
      : paths.classRecommendations
  }, [
    cookieRedirectHref,
    itemUuid,
    itemType,
    categoryId,
    addItemHref,
    isPromotionOn,
  ])
}

// Check if we have the category page context before returning the ctaegory, otherwise it errors out
const useCategoryFromPageData = () => {
  const context = useContext(PageDataContext)

  if (!context) {
    return null
  }

  return context.category
}

const useSource = () => {
  const routerContext = useContext(RouterContext)

  if (routerContext) {
    return inferPageSource(routerContext.pathname)
  }

  return getPageSource()
}

const useQuizPrimerItem = () => {
  const source = useSource()

  const [item, setItem] = useState<
    SingleSessionResult | GrowthCategory | GrowthContent | null | undefined
  >()
  const [category, setCategory] = useState<GrowthCategory | null | undefined>()
  const [itemType, setItemType] = useState<string | null | undefined>()

  // - category page
  const categoryFromCategoryPage = useCategoryFromPageData()

  const {
    // - subcategory page
    subcategory,
    primaryCategory: primarySubcategoryCategory,
    // - cm pages
    //   - class/session page
    growthClass,
    primaryGrowthCatAndSubcat: primaryCatAndSubcatFromClassPage,
    //   - camp page
    session,
    primaryCatAndSubcat: primaryCatAndSubcatFromCampPage,
  } = useStaticData<{
    category?: GrowthCategory
    growthClass?: GrowthContent
    primaryGrowthCatAndSubcat?: {
      primaryCategory: GrowthCategory
    }
    primaryCatAndSubcat?: {
      primaryCategory: GrowthCategory
    }
    session?: SingleSessionResult
    subcategory?: GrowthCategory
    primaryCategory?: GrowthCategory
  }>()

  useEffect(() => {
    if (source === PageSource.SUBCATEGORY) {
      setItem(subcategory)
      setCategory(primarySubcategoryCategory)
      setItemType('subcategory')
    } else if (source === PageSource.CATEGORY) {
      setItem(categoryFromCategoryPage)
      setCategory(categoryFromCategoryPage)
      setItemType('category')
    } else if (growthClass) {
      setItem(growthClass)
      setCategory(primaryCatAndSubcatFromClassPage?.primaryCategory)
      setItemType(growthClass?.type)
    } else if (session) {
      setItem(session)
      setCategory(primaryCatAndSubcatFromCampPage?.primaryCategory)
      setItemType('camp')
    }
  }, [
    source,
    subcategory,
    categoryFromCategoryPage,
    growthClass,
    session,
    primaryCatAndSubcatFromClassPage,
    primaryCatAndSubcatFromCampPage,
    primarySubcategoryCategory,
  ])

  return {
    item,
    category,
    itemType,
  }
}
